import {ensurePreviousActiveDialogIsClosed} from '@github-ui/diff-lines/ensure-previous-active-dialog-is-closed'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {AlertIcon} from '@primer/octicons-react'
import {Button, IconButton} from '@primer/react'
import {useRef, useState} from 'react'

import {AlertsSidePanel} from './AlertsSidePanel'
import {useAnnotations} from '../../stores/selectors'
import type {PageLimits} from '../../page-data/payloads/files'
import {usePullRequestAnalytics} from '../../hooks/use-pull-request-analytics'
import {useDockedPanel, useDockedPanelActions} from '../../contexts/DockedPanelContext'
import {isFeatureEnabled} from '@github-ui/feature-flags'

export interface OpenAlertsPanelButtonProps {
  isFileTreeExpanded?: boolean
  pageLimits: PageLimits
  className?: string
}

/**
 *
 * Renders a button that opens the alerts side panel
 * Contains the total number of alerts plus the icon
 */
export function OpenAlertsPanelButton({isFileTreeExpanded, pageLimits, className}: OpenAlertsPanelButtonProps) {
  const isPrsDockedSidePanelEnabled = isFeatureEnabled('prs_docked_side_panel')
  const panelName = useDockedPanel()
  const alertsDockedSidePanelIsOpen = panelName === 'alerts'

  const [alertsSidePanelIsOpen, setAlertsSidePanelIsOpen] = useState(false)
  const toggleSidePanelRef = useRef<HTMLButtonElement>(null)
  const actions = useDockedPanelActions()

  const alerts = useAnnotations()
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  if (!alerts) {
    return null
  }

  const totalAlertsCount = alerts.length

  return totalAlertsCount > 0 ? (
    <div className="d-flex flex-items-center">
      <ErrorBoundary
        boundaryName="OpenAlertsPanelButton"
        fallback={
          <IconButton
            inactive
            aria-label="The alerts side panel cannot currently be opened."
            icon={AlertIcon}
            size="small"
            variant="invisible"
          />
        }
      >
        <Button
          ref={toggleSidePanelRef}
          className={className}
          aria-label="Open alerts side panel"
          count={totalAlertsCount}
          leadingVisual={AlertIcon}
          size="small"
          onClick={() => {
            sendPullRequestAnalyticsEvent('alerts_side_panel.open', 'ALERTS_SIDE_PANEL_BUTTON')
            if (isPrsDockedSidePanelEnabled) {
              actions.togglePanel('alerts', toggleSidePanelRef.current)
            } else {
              ensurePreviousActiveDialogIsClosed()
              setAlertsSidePanelIsOpen(true)
            }
          }}
        >
          {isFileTreeExpanded && <span className="d-none d-xl-block">Alerts</span>}
        </Button>
        <AlertsSidePanel
          annotations={alerts}
          isOpen={alertsSidePanelIsOpen || alertsDockedSidePanelIsOpen}
          pageLimits={pageLimits}
          returnFocusRef={toggleSidePanelRef}
          onClose={() => {
            if (isPrsDockedSidePanelEnabled) {
              actions.closePanel()
            } else {
              setAlertsSidePanelIsOpen(false)
            }
          }}
        />
      </ErrorBoundary>
    </div>
  ) : null
}
